// src/components/Admin/Prompts.tsx
import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Fade,
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  Stop as StopIcon,
  VolumeUp as VolumeIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Upload as UploadIcon,
  Refresh as RefreshIcon,
  Save as SaveIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import api from '../../services/api';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { useAuthWithCustomerOverride } from '../../hooks/useAuthWithCustomerOverride';
import { useCustomerFilter } from '../../hooks/useCustomerFilter';
import errorLogger from '../../services/errorLogger.service';

// Create logger for this component
const logger = errorLogger.createComponentLogger('Prompts');

interface Voice {
  voice_id: string;
  name: string;
  category: string;
  preview_url?: string;
}

interface Recording {
  RecordID: number;
  Name: string;
  Notes?: string;
  Created: string;
  deleted: boolean;
}

const Prompts: React.FC = () => {
  const authOverride = useAuthWithCustomerOverride();
  const { member, isLoginAsCustomer } = authOverride;

  logger.debug('Component init - authOverride:', {
    authOverride,
    member,
    isLoginAsCustomer,
    memberCustomerID: member?.customerID
  });

  const customerFilter = useCustomerFilter();
  const audioRef = useRef<HTMLAudioElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const blobUrlRef = useRef<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');
  
  // Check for login-as-customer token and session data
  const urlParams = new URLSearchParams(window.location.search);
  const hasLoginToken = urlParams.has('lat');
  const hasSessionData = sessionStorage.getItem('loginAsCustomerId') && sessionStorage.getItem('loginAsCustomerName');
  
  // Initialize waiting state based on token presence
  const [waitingForLoginAs] = useState(() => {
    const shouldWait = hasLoginToken && !hasSessionData;
    if (shouldWait) {
      logger.debug('Initializing with waiting=true due to login token');
    }
    return shouldWait;
  });
  
  // TTS State
  const [text, setText] = useState('');
  const [selectedVoice, setSelectedVoice] = useState('');
  const [voices, setVoices] = useState<Voice[]>([]);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const voiceSettings = {
    stability: 0.5,
    similarity_boost: 0.75,
    style: 0,
    use_speaker_boost: true,
  };
  const [fileName, setFileName] = useState('');
  const [notes, setNotes] = useState('');
  const [savingAudio, setSavingAudio] = useState(false);
  const [cacheKey, setCacheKey] = useState<string | null>(null);
  const [hasGeneratedAudio, setHasGeneratedAudio] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  
  // Recordings List State
  const [recordings, setRecordings] = useState<Recording[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRecordings, setTotalRecordings] = useState(0);
  const [loadingRecordings, setLoadingRecordings] = useState(false);
  
  // Upload State
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadFileName, setUploadFileName] = useState('');
  const [uploadNotes, setUploadNotes] = useState('');
  const [uploading, setUploading] = useState(false);
  
  // Edit State
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingRecording, setEditingRecording] = useState<Recording | null>(null);
  const [editNotes, setEditNotes] = useState('');
  
  // Playback State
  const [playingRecordingId, setPlayingRecordingId] = useState<number | null>(null);

  // Helper function to show snackbar
  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  // Helper function to get customer params - matching IVR pattern
  const getCustomerParams = () => {
    const params: any = {};

    logger.debug('getCustomerParams - Debug:', {
      isLoginAsCustomer,
      memberCustomerID: member?.customerID,
      memberType: member?.memberType,
      customerFilterIsFiltered: customerFilter.isFiltered,
      customerFilterCustomerId: customerFilter.customerId
    });

    if (isLoginAsCustomer && member?.customerID) {
      params.customerId = member.customerID;
      logger.debug(`Using login-as customerID: ${member.customerID}`);
    } else if (customerFilter.isFiltered && (member?.memberType === 'master_admin' || member?.memberType === 'reseller_admin')) {
      params.customerId = customerFilter.customerId;
      logger.debug(`Using filter customerID: ${customerFilter.customerId}`);
    } else {
      logger.debug('No customer override - will use JWT');
    }

    logger.debug('Final params:', params);
    return params;
  };

  // Fetch recordings on component mount and when pagination changes
  useEffect(() => {
    if (!waitingForLoginAs && member) {
      // Add delay to ensure login-as context is ready
      const timer = setTimeout(() => {
        fetchRecordings();
      }, 300);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, waitingForLoginAs, member, isLoginAsCustomer]);

  // Fetch available voices on component mount
  useEffect(() => {
    if (!waitingForLoginAs && member) {
      // Add delay to ensure login-as context is ready
      const timer = setTimeout(() => {
        fetchVoices();
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [waitingForLoginAs, member, isLoginAsCustomer]);
  
  // Listen for login-as-customer changes
  useEffect(() => {
    const handleLoginAsChange = () => {
      logger.debug('Login-as-customer changed, refreshing data');
      fetchRecordings();
      fetchVoices();
    };
    
    window.addEventListener('loginAsCustomerChanged', handleLoginAsChange);
    return () => window.removeEventListener('loginAsCustomerChanged', handleLoginAsChange);
  }, []);
  
  // Update browser tab title with customer info
  useEffect(() => {
    if (customerFilter.isFiltered && customerFilter.customerId && customerFilter.customerName) {
      document.title = `${customerFilter.customerId} - ${customerFilter.customerName} - Prompts`;
    } else {
      document.title = 'Prompts - Admin Portal';
    }
  }, [customerFilter]);

  const fetchVoices = async () => {
    // Safety check - don't fetch if we're waiting for login-as data
    if (waitingForLoginAs) {
      logger.debug('Skipping voices fetch - waiting for login-as data');
      return;
    }

    try {
      const params = getCustomerParams();

      logger.debug('Fetching voices with params:', params);
      const response = await api.get('/endpoints/prompts/voices', { params });
      if (response.data.success) {
        setVoices(response.data.data);
        // Set default voice if available and none is selected
        if (response.data.data.length > 0 && !selectedVoice) {
          setSelectedVoice(response.data.data[0].voice_id);
        }
      }
    } catch (err: any) {
      logger.error('Failed to fetch voices:', err);
      setError('Failed to load available voices');
    }
  };

  const fetchRecordings = async () => {
    // Safety check - don't fetch if we're waiting for login-as data
    if (waitingForLoginAs) {
      logger.debug('Skipping recordings fetch - waiting for login-as data');
      return;
    }

    setLoadingRecordings(true);
    try {
      const params: any = {
        page: String(page + 1),
        limit: String(rowsPerPage),
      };

      // Add customer filter - EXACTLY like IVR does it
      if (isLoginAsCustomer && member?.customerID) {
        logger.debug(`LOGIN AS MODE - Using customerID: ${member.customerID}`);
        params.customerId = member.customerID;
      } else if (customerFilter.isFiltered && (member?.memberType === 'master_admin' || member?.memberType === 'reseller_admin')) {
        logger.debug(`FILTER MODE - Using customerFilter: ${customerFilter.customerId}`);
        params.customerId = customerFilter.customerId;
      } else {
        logger.debug('NO FILTER - Will use JWT customer');
      }

      logger.debug('Fetching recordings with params:', params);
      const response = await api.get('/endpoints/prompts/recordings', { params });
      
      if (response.data.success) {
        setRecordings(response.data.data.data || []);
        setTotalRecordings(response.data.data.total || 0);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load recordings');
    } finally {
      setLoadingRecordings(false);
    }
  };

  const generateAudio = async () => {
    if (!text.trim()) {
      setError('Please enter some text');
      return;
    }

    if (!selectedVoice) {
      setError('Please select a voice');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      // Get customer params for API call
      const params = getCustomerParams();
      
      const response = await api.post('/endpoints/prompts/generate', {
        text: text.trim(),
        voice_id: selectedVoice,
        voice_settings: voiceSettings,
      }, { params });

      logger.debug('TTS Response:', response.data);
      
      if (response.data.success) {
        // The response is wrapped in data property by Response::success()
        const responseData = response.data.data;
        
        if (responseData && responseData.audio_url) {
          setAudioUrl(responseData.audio_url);
          setCacheKey(responseData.cache_key);
          setHasGeneratedAudio(true);
          showSnackbar(responseData.cached ? 'Audio retrieved from cache!' : 'Audio generated successfully!', 'success');
        } else {
          showSnackbar('Audio generated but URL not received', 'error');
          logger.error('Invalid response structure:', response.data);
        }
      }
    } catch (err: any) {
      showSnackbar(err.response?.data?.message || 'Failed to generate audio', 'error');
    } finally {
      setLoading(false);
    }
  };

  const playAudio = async () => {
    logger.debug(`playAudio called - audioUrl: ${audioUrl}, cacheKey: ${cacheKey}`);
    if (!audioUrl || !cacheKey) {
      logger.error('Missing audioUrl or cacheKey');
      return;
    }
    
    if (isPlaying) {
      audioRef.current?.pause();
      setIsPlaying(false);
      return;
    }
    
    try {
      // Get customer params for API call
      const params = getCustomerParams();

      // Fetch the audio with authentication
      logger.debug(`Fetching audio from: /endpoints/prompts/preview/${cacheKey}.mp3`);
      const response = await api.get(`/endpoints/prompts/preview/${cacheKey}.mp3`, {
        responseType: 'blob',
        params
      });
      
      // Clean up previous blob URL if exists
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
      }
      
      // Create a blob URL from the response
      const audioBlob = new Blob([response.data], { type: 'audio/mpeg' });
      const blobUrl = URL.createObjectURL(audioBlob);
      blobUrlRef.current = blobUrl;
      
      // Set the blob URL as the audio source
      if (audioRef.current) {
        audioRef.current.src = blobUrl;
        audioRef.current.play();
        setIsPlaying(true);
      }
    } catch (error) {
      logger.error('Failed to load audio:', error);
      showSnackbar('Failed to load audio preview', 'error');
    }
  };

  const saveToAsterisk = async () => {
    if (!audioUrl || !cacheKey || !fileName.trim()) {
      setError('Please generate audio and enter a filename first');
      return;
    }

    // Validate filename (alphanumeric and underscores only)
    if (!/^[a-zA-Z0-9_]+$/.test(fileName)) {
      setError('Filename can only contain letters, numbers, and underscores');
      return;
    }

    setSavingAudio(true);
    setError(null);
    setSuccess(null);

    try {
      logger.debug('Saving audio with:', { cache_key: cacheKey, filename: fileName, notes: notes });
      // Get customer params for API call
      const params = getCustomerParams();
      
      const response = await api.post('/endpoints/prompts/save', {
        cache_key: cacheKey,
        filename: fileName,
        notes: notes.trim() || undefined,
      }, { params });

      logger.debug('Save response:', response.data);

      if (response.data.success) {
        showSnackbar(`Audio saved as ${response.data.filename} and added to Recordings`, 'success');
        // Reset form
        setText('');
        setFileName('');
        setNotes('');
        setAudioUrl(null);
        setCacheKey(null);
        setHasGeneratedAudio(false);
        setCurrentStep(0);
        // Refresh recordings list
        fetchRecordings();
        // Switch to recordings tab
        setTabValue(0);
      }
    } catch (err: any) {
      showSnackbar(err.response?.data?.message || 'Failed to save audio', 'error');
    } finally {
      setSavingAudio(false);
    }
  };

  const handleUpload = async () => {
    if (!uploadFile || !uploadFileName.trim()) {
      setError('Please select a file and enter a filename');
      return;
    }

    if (!/^[a-zA-Z0-9_]+$/.test(uploadFileName)) {
      setError('Filename can only contain letters, numbers, and underscores');
      return;
    }

    setUploading(true);
    setError(null);
    setSuccess(null);

    const formData = new FormData();
    formData.append('file', uploadFile);
    formData.append('filename', uploadFileName);
    formData.append('notes', uploadNotes);

    try {
      // Get customer params for API call
      const params = getCustomerParams();
      
      const response = await api.post('/endpoints/prompts/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        params
      });

      if (response.data.success) {
        setSuccess('Recording uploaded successfully');
        setUploadDialogOpen(false);
        setUploadFile(null);
        setUploadFileName('');
        setUploadNotes('');
        fetchRecordings();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to upload recording');
    } finally {
      setUploading(false);
    }
  };

  const handleEdit = (recording: Recording) => {
    setEditingRecording(recording);
    setEditNotes(recording.Notes || '');
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!editingRecording) return;

    try {
      // Get customer params for API call
      const params = getCustomerParams();
      
      const response = await api.put(`/endpoints/prompts/recordings/${editingRecording.RecordID}`, {
        notes: editNotes,
      }, { params });

      if (response.data.success) {
        setSuccess('Recording updated successfully');
        setEditDialogOpen(false);
        setEditingRecording(null);
        fetchRecordings();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update recording');
    }
  };

  const handleDelete = async (recording: Recording) => {
    if (!window.confirm(`Are you sure you want to delete "${recording.Name}"? It will be moved to trash and permanently deleted after 90 days.`)) {
      return;
    }

    try {
      // Get customer params for API call
      const params = getCustomerParams();
      
      const response = await api.delete(`/endpoints/prompts/recordings/${recording.RecordID}`, { params });
      
      if (response.data.success) {
        setSuccess('Recording moved to trash');
        fetchRecordings();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete recording');
    }
  };

  const handlePlayRecording = async (recording: Recording) => {
    // If this recording is already playing, stop it
    if (playingRecordingId === recording.RecordID) {
      audioRef.current?.pause();
      setIsPlaying(false);
      setPlayingRecordingId(null);
      return;
    }
    
    try {
      // Stop any currently playing audio
      if (audioRef.current && !audioRef.current.paused) {
        audioRef.current.pause();
      }
      
      // Directly use the stream endpoint
      logger.debug(`Fetching audio stream for recording: ${recording.RecordID}`);

      // Get customer params for API call
      const params = getCustomerParams();

      // Use the stream endpoint directly with axios which handles auth
      const response = await api.get(`/endpoints/prompts/stream/${recording.RecordID}`, {
        responseType: 'blob',
        params
      });
      
      logger.debug(`Audio response received, blob size: ${response.data.size}, type: ${response.data.type}`);
      
      // Check if blob is empty
      if (response.data.size === 0) {
        throw new Error('Received empty audio file');
      }
      
      // Clean up previous blob URL if exists
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
      }
      
      // Create blob URL
      const blobUrl = URL.createObjectURL(response.data);
      blobUrlRef.current = blobUrl;
      logger.debug(`Created blob URL: ${blobUrl}`);
      
      logger.debug('audioRef.current exists?', !!audioRef.current);
      logger.debug('audioRef:', audioRef);
      
      if (audioRef.current) {
        // Remove all existing event listeners first
        audioRef.current.onloadeddata = null;
        audioRef.current.onerror = null;
        audioRef.current.oncanplay = null;
        audioRef.current.onplay = null;
        audioRef.current.onplaying = null;
        audioRef.current.onloadstart = null;
        audioRef.current.onloadedmetadata = null;
        
        logger.debug('Audio element before setting src:', {
          currentSrc: audioRef.current.currentSrc,
          readyState: audioRef.current.readyState,
          paused: audioRef.current.paused,
          volume: audioRef.current.volume,
          muted: audioRef.current.muted
        });
        
        // Add event listeners before setting src
        audioRef.current.onloadstart = () => {
          logger.debug('Audio loadstart event');
        };
        
        audioRef.current.onloadedmetadata = () => {
          logger.debug(`Audio metadata loaded, duration: ${audioRef.current?.duration}`);
        };
        
        audioRef.current.onloadeddata = () => {
          logger.debug('Audio data loaded successfully');
        };
        
        audioRef.current.onerror = (e) => {
          logger.error('Audio element error:', e);
          logger.error('Audio error details:', {
            error: audioRef.current?.error,
            networkState: audioRef.current?.networkState,
            readyState: audioRef.current?.readyState
          });
          setError('Failed to play audio file');
          setPlayingRecordingId(null);
        };
        
        audioRef.current.oncanplay = () => {
          logger.debug('Audio can play');
        };
        
        audioRef.current.oncanplaythrough = () => {
          logger.debug('Audio can play through');
        };
        
        audioRef.current.onplay = () => {
          logger.debug('Audio play event fired');
        };
        
        audioRef.current.onplaying = () => {
          logger.debug('Audio playing event fired');
        };
        
        audioRef.current.onpause = () => {
          logger.debug('Audio paused');
        };
        
        audioRef.current.onstalled = () => {
          logger.debug('Audio stalled');
        };
        
        audioRef.current.onsuspend = () => {
          logger.debug('Audio suspended');
        };
        
        audioRef.current.onwaiting = () => {
          logger.debug('Audio waiting');
        };
        
        // Set the source
        audioRef.current.src = blobUrl;
        logger.debug(`Audio element src set to: ${blobUrl}`);
        
        // Force load
        audioRef.current.load();
        logger.debug('Called load() on audio element');
        
        // Wait a bit then try to play
        setTimeout(async () => {
          try {
            logger.debug('Audio element state before play:', {
              readyState: audioRef.current?.readyState,
              networkState: audioRef.current?.networkState,
              error: audioRef.current?.error,
              currentTime: audioRef.current?.currentTime,
              duration: audioRef.current?.duration,
              paused: audioRef.current?.paused
            });
            
            if (audioRef.current) {
              logger.debug('Attempting to play audio...');
              const playPromise = audioRef.current.play();
              
              if (playPromise !== undefined) {
                playPromise
                  .then(() => {
                    logger.debug('Play promise resolved - audio should be playing');
                    setIsPlaying(true);
                    setPlayingRecordingId(recording.RecordID);
                  })
                  .catch((error) => {
                    logger.error('Play promise rejected:', error);
                    logger.error(`Play error name: ${error.name}`);
                    logger.error(`Play error message: ${error.message}`);
                    setError(`Failed to play audio: ${error.message}`);
                    setPlayingRecordingId(null);
                  });
              }
            }
          } catch (playError) {
            logger.error('Error in play attempt:', playError);
            setError('Failed to play audio');
            setPlayingRecordingId(null);
          }
        }, 100);
      }
    } catch (err: any) {
      logger.error('Error playing recording:', err);
      logger.error('Error details:', {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status
      });
      setError(err.response?.data?.message || err.message || 'Failed to play recording');
      setPlayingRecordingId(null);
    }
  };

  const handleAudioEnded = () => {
    setIsPlaying(false);
    setPlayingRecordingId(null);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Voice Prompts & Recordings
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Generate voice prompts using text-to-speech, upload recordings, or dial #8999 from any phone to record
        </Typography>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Recordings List */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Recordings</Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => {
                setTabValue(1);
                // Reset form when opening create prompt
                setCurrentStep(0);
                setText('');
                setFileName('');
                setNotes('');
                // Keep the selected voice or use the first available
                if (!selectedVoice && voices.length > 0) {
                  setSelectedVoice(voices[0].voice_id);
                }
                setAudioUrl(null);
                setHasGeneratedAudio(false);
              }}
            >
              Create Recording
            </Button>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={fetchRecordings}
              >
                Refresh
              </Button>
              <Button
                variant="contained"
                startIcon={<UploadIcon />}
                onClick={() => setUploadDialogOpen(true)}
              >
                Upload Recording
              </Button>
            </Box>
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>File Name</TableCell>
                  <TableCell>Notes</TableCell>
                  <TableCell align="center">Created</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loadingRecordings ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : recordings.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      No recordings found
                    </TableCell>
                  </TableRow>
                ) : (
                  recordings.map((recording) => (
                    <TableRow key={recording.RecordID}>
                      <TableCell>{recording.Name}</TableCell>
                      <TableCell>{recording.Notes || '-'}</TableCell>
                      <TableCell align="center">
                        {new Date(recording.Created).toLocaleDateString()}
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={() => handlePlayRecording(recording)}
                          title={playingRecordingId === recording.RecordID ? "Stop" : "Play"}
                          color={playingRecordingId === recording.RecordID ? "primary" : "default"}
                        >
                          {playingRecordingId === recording.RecordID ? <StopIcon /> : <PlayIcon />}
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleEdit(recording)}
                          title="Edit"
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDelete(recording)}
                          title="Delete"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[10, 25, 50]}
            component="div"
            count={totalRecordings}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>

      {/* Create Recording Dialog */}
      <Dialog 
        open={tabValue === 1} 
        onClose={() => setTabValue(0)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h5">
            Create Voice Recording
          </Typography>
        </DialogTitle>
        <DialogContent>
          {/* Step 1: Filename */}
          {currentStep === 0 && (
            <Box>
              <Typography variant="h5" gutterBottom>
                Step 1: Name Your Prompt
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                Let's start by giving your prompt a descriptive filename.
              </Typography>
              <TextField
                fullWidth
                label="Filename (no extension)"
                value={fileName}
                onChange={(e) => {
                  const value = e.target.value;
                  // Only allow letters, numbers, and underscores
                  if (/^[a-zA-Z0-9_]*$/.test(value)) {
                    setFileName(value);
                  }
                }}
                placeholder="e.g., welcome_message, business_hours, main_menu"
                helperText={
                  fileName && !/^[a-zA-Z0-9_]+$/.test(fileName)
                    ? "Filename can only contain letters, numbers, and underscores"
                    : "Use letters, numbers, and underscores only"
                }
                error={fileName.length > 0 && !/^[a-zA-Z0-9_]+$/.test(fileName)}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="Notes (optional)"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Brief description of this prompt's purpose"
                multiline
                rows={2}
                sx={{ mb: 3 }}
              />
              <Button
                variant="contained"
                onClick={() => setCurrentStep(1)}
                disabled={!fileName.trim() || !/^[a-zA-Z0-9_]+$/.test(fileName)}
                fullWidth
                size="large"
              >
                Next: Choose Voice
              </Button>
            </Box>
          )}

          {/* Step 2: Voice Selection */}
          {currentStep === 1 && (
            <Box>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
                Creating: <strong>{fileName}</strong>
              </Typography>
              <Typography variant="h5" gutterBottom>
                Step 2: Choose a Voice
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                Select a voice that best represents your brand. Click on any voice to hear a sample.
              </Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                {voices.map((voice) => (
                  <Grid item xs={12} sm={6} md={4} key={voice.voice_id}>
                    <Card 
                      sx={{ 
                        cursor: 'pointer',
                        border: selectedVoice === voice.voice_id ? 2 : 0,
                        borderColor: 'primary.main',
                        transition: 'all 0.2s',
                        '&:hover': { boxShadow: 3 }
                      }}
                      onClick={() => {
                        setSelectedVoice(voice.voice_id);
                        // Play sample if available
                        if (voice.preview_url && audioRef.current) {
                          audioRef.current.src = voice.preview_url;
                          audioRef.current.play();
                        }
                      }}
                    >
                      <CardContent>
                        <Typography variant="h6">{voice.name}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {voice.category}
                        </Typography>
                        <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
                          "My name is {voice.name} and I can help you make prompts to play to your callers."
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="outlined"
                  onClick={() => setCurrentStep(0)}
                  sx={{ flex: 1 }}
                >
                  Back
                </Button>
                <Button
                  variant="contained"
                  onClick={() => setCurrentStep(2)}
                  disabled={!selectedVoice}
                  sx={{ flex: 1 }}
                >
                  Next: Enter Text
                </Button>
              </Box>
            </Box>
          )}

          {/* Step 3: Text Entry */}
          {currentStep === 2 && (
            <Box>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
                File: <strong>{fileName}</strong> | Voice: <strong>{voices.find(v => v.voice_id === selectedVoice)?.name}</strong>
              </Typography>
              <Typography variant="h5" gutterBottom>
                Step 3: Enter Your Message
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                Type the message you want your callers to hear.
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={8}
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Thank you for calling. Your call is important to us..."
                variant="outlined"
                inputProps={{ maxLength: 400 }}
                helperText={`${text.length} / 400 characters`}
                error={text.length > 400}
                sx={{ mb: 3 }}
              />
              
              {/* Voice Settings */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  Voice Selection
                </Typography>
                <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                  <InputLabel>Voice</InputLabel>
                  <Select
                    value={selectedVoice}
                    onChange={(e) => setSelectedVoice(e.target.value)}
                    label="Voice"
                  >
                    {voices.map((voice) => (
                      <MenuItem key={voice.voice_id} value={voice.voice_id}>
                        {voice.name} ({voice.category})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              {!hasGeneratedAudio ? (
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button
                    variant="outlined"
                    onClick={() => setCurrentStep(1)}
                    sx={{ flex: 1 }}
                  >
                    Back
                  </Button>
                  <Button
                    variant="contained"
                    onClick={generateAudio}
                    disabled={loading || !text.trim()}
                    startIcon={loading ? <CircularProgress size={20} /> : <VolumeIcon />}
                    sx={{ flex: 2 }}
                  >
                    {loading ? 'Generating...' : 'Generate Audio'}
                  </Button>
                </Box>
              ) : (
                <Box>
                  <Alert severity="success" sx={{ mb: 2 }}>
                    Audio generated successfully! Preview your prompt below.
                  </Alert>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button
                      variant="outlined"
                      onClick={() => {
                        setHasGeneratedAudio(false);
                        setAudioUrl(null);
                      }}
                    >
                      Regenerate
                    </Button>
                    <Button
                      variant="contained"
                      onClick={playAudio}
                      disabled={loading}
                      startIcon={isPlaying ? <StopIcon /> : <PlayIcon />}
                      sx={{ flex: 1 }}
                    >
                      {isPlaying ? 'Stop' : 'Play Audio'}
                    </Button>
                    <Button
                      variant="contained"
                      color="success"
                      onClick={saveToAsterisk}
                      disabled={savingAudio}
                      startIcon={savingAudio ? <CircularProgress size={20} /> : <SaveIcon />}
                      sx={{ flex: 1 }}
                    >
                      {savingAudio ? 'Saving...' : 'Save to System'}
                    </Button>
                  </Box>
                </Box>
              )}
            </Box>
          )}

        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTabValue(0)} color="secondary">
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Audio element for playback */}
      <audio
        ref={audioRef}
        onEnded={handleAudioEnded}
        style={{ display: 'none' }}
      />

      {/* Upload Dialog */}
      <Dialog open={uploadDialogOpen} onClose={() => setUploadDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Upload Recording</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <input
              ref={fileInputRef}
              type="file"
              accept="audio/*"
              style={{ display: 'none' }}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  setUploadFile(file);
                  // Auto-fill filename from file name
                  const name = file.name.replace(/\.[^/.]+$/, '').replace(/[^a-zA-Z0-9_]/g, '_');
                  setUploadFileName(name);
                }
              }}
            />
            <Button
              fullWidth
              variant="outlined"
              onClick={() => fileInputRef.current?.click()}
              sx={{ mb: 2 }}
            >
              {uploadFile ? uploadFile.name : 'Select Audio File'}
            </Button>

            <TextField
              fullWidth
              label="Filename (no extension)"
              value={uploadFileName}
              onChange={(e) => {
                const value = e.target.value;
                // Only allow letters, numbers, and underscores
                if (/^[a-zA-Z0-9_]*$/.test(value)) {
                  setUploadFileName(value);
                }
              }}
              placeholder="e.g., welcome_message"
              helperText={
                uploadFileName && !/^[a-zA-Z0-9_]+$/.test(uploadFileName)
                  ? "Filename can only contain letters, numbers, and underscores"
                  : "Use letters, numbers, and underscores only"
              }
              error={uploadFileName.length > 0 && !/^[a-zA-Z0-9_]+$/.test(uploadFileName)}
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              label="Notes (optional)"
              value={uploadNotes}
              onChange={(e) => setUploadNotes(e.target.value)}
              placeholder="Description of this recording"
              multiline
              rows={3}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleUpload}
            variant="contained"
            disabled={uploading || !uploadFile || !uploadFileName.trim() || !/^[a-zA-Z0-9_]+$/.test(uploadFileName)}
            startIcon={uploading && <CircularProgress size={20} />}
          >
            {uploading ? 'Uploading...' : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Recording</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              File: {editingRecording?.Name}
            </Typography>
            <TextField
              fullWidth
              label="Notes"
              value={editNotes}
              onChange={(e) => setEditNotes(e.target.value)}
              placeholder="Description of this recording"
              multiline
              rows={3}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveEdit} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        TransitionComponent={Fade}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
          variant="filled"
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Prompts;